/**
 * Puente con la app nativa de iPad.
 *
 * En el iPad, el Apple Pencil no se captura en el navegador sino en una capa
 * nativa por encima (Metal, 120 Hz, con predicción de trazo). Cuando el trazo
 * termina, la app lo entrega aquí y se guarda en el tablero como cualquier otro,
 * de modo que se sincroniza con todos por Yjs.
 *
 * Del lado web solo hay que: avisar el estilo activo y recibir el trazo.
 */
import type { InkPoint } from '../lib/freehand.ts'
import { PRESSURE_THINNING, type ToolId } from '../state/store.ts'
import { commitStroke } from '../tools/commit-stroke.ts'
import { displayInkFor } from '../canvas/render-shape.ts'
import type { AppContext } from '../tools/types.ts'

const TOOLS: ToolId[] = ['select', 'hand', 'draw', 'highlighter', 'eraser', 'note', 'text', 'shape', 'image', 'laser']

/** Nombre del manejador de mensajes que instala la app nativa. */
const HANDLER = 'pizarra'

interface WebKitBridge {
  messageHandlers?: Record<string, { postMessage(message: unknown): void } | undefined>
}

interface NativeStrokePayload {
  /** puntos en píxeles CSS del webview: [x, y, presión, ...] */
  points: number[]
  color: string
  size: number
  highlighter?: boolean
  /** sensibilidad a la presión con la que se dibujó (0 = grosor parejo) */
  thinning?: number
  /**
   * Identificador del trazo retenido en la capa nativa. La app lo mantiene
   * pintado hasta recibir el acuse: si lo borrara al entregarlo, habría unos
   * cuadros en los que nadie dibuja el trazo y se vería un parpadeo.
   */
  ackId?: string
}

declare global {
  interface Window {
    webkit?: WebKitBridge
    pizarraNative?: NativeApi
  }
}

/** ¿La capa nativa está capturando el lápiz ahora mismo? */
let nativeInkActive = false

export function isNativeInkActive(): boolean {
  return nativeInkActive
}

let nativePenDown = false
// Menos infinito = "nunca": con 0, recién cargada la página parecería que el
// lápiz acababa de levantarse y los primeros toques de dedo se ignorarían.
let nativePenUpAt = Number.NEGATIVE_INFINITY

export function isNativePenDown(): boolean {
  return nativePenDown
}

/** ¿El lápiz nativo está abajo o acaba de levantarse? Sirve para rechazar la palma. */
export function isNativePenRecent(): boolean {
  return nativePenDown || performance.now() - nativePenUpAt < 900
}

/**
 * Rastro remoto para depurar el iPad sin cables: cae en /api/diag del servidor.
 * Apagado por defecto (antes mandaba un POST por trazo y hasta uno por cuadro
 * al hacer zoom). Se enciende con localStorage.setItem('wb.diag', '1').
 */
function diag(message: string): void {
  try {
    if (localStorage.getItem('wb.diag') !== '1') return
  } catch {
    return
  }
  try {
    void fetch('/api/diag', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ source: 'web-ipad', events: [message] }),
    }).catch(() => {})
  } catch {
    /* entorno sin fetch (pruebas) */
  }
}

export interface NativeApi {
  /** La app entrega un trazo terminado (coordenadas de pantalla). */
  stroke(payload: NativeStrokePayload | string): void
  undo(): void
  redo(): void
  /** Cambia de herramienta desde un control nativo. */
  setTool(tool: string): void
  /**
   * La app avisa si está capturando el lápiz. Solo entonces la web deja de
   * atender eventos de lápiz; si el puente fallara, la web sigue funcionando.
   */
  setNativeInk(active: boolean): void
  /** El lápiz tocó o dejó la pantalla (rechazo de palma en la web). */
  setPenDown(down: boolean): void
  /** Estado actual, por si la app lo necesita al arrancar. */
  state(): string
}

export function isNativeShell(): boolean {
  return typeof window !== 'undefined' && !!window.webkit?.messageHandlers?.[HANDLER]
}

function post(message: unknown): void {
  window.webkit?.messageHandlers?.[HANDLER]?.postMessage(message)
}

/**
 * Conecta la app web con el contenedor nativo. No hace nada en un navegador normal.
 */
export function connectNativeBridge(ctx: AppContext): () => void {
  if (!isNativeShell()) return () => {}

  document.documentElement.classList.add('is-native-ipad')
  diag('puente nativo conectado')

  const currentStyle = (): {
    color: string
    size: number
    highlighter: boolean
    tool: string
    thinning: number
  } => {
    const { style, tool } = ctx.store
    const highlighter = tool === 'highlighter'
    return {
      tool,
      highlighter,
      // El color de EXHIBICIÓN: en modo oscuro la pluma negra se pinta clara
      // también en la capa nativa, o el trazo en vivo sería invisible.
      color: highlighter ? style.highlighterColor : displayInkFor(style.inkColor, ctx.store.darkMode),
      size: highlighter ? style.highlighterSize : style.inkSize,
      // La app dibuja la vista previa con la misma sensibilidad que se guardará.
      thinning: highlighter ? 0 : (PRESSURE_THINNING[style.penPressure] ?? 0.55),
    }
  }

  const api: NativeApi = {
    stroke(payload) {
      const data: NativeStrokePayload = typeof payload === 'string' ? JSON.parse(payload) : payload
      if (!data?.points || data.points.length < 3) return
      diag(`trazo recibido del lápiz: ${Math.floor(data.points.length / 3)} puntos`)
      const points: InkPoint[] = []
      for (let i = 0; i + 2 < data.points.length; i += 3) {
        // La app manda píxeles de pantalla; aquí se pasan a coordenadas de mundo.
        const world = ctx.renderer.screenToWorld(data.points[i], data.points[i + 1])
        points.push({ x: world.x, y: world.y, p: data.points[i + 2] })
      }
      // El color guardado es el REAL de la pluma, no el de exhibición: la capa
      // nativa pinta con el color remapeado del modo oscuro y ese no debe
      // quedar en los datos (en modo claro se vería un trazo blanco invisible).
      const colorReal = data.highlighter ? ctx.store.style.highlighterColor : ctx.store.style.inkColor
      const shape = commitStroke(
        ctx,
        points,
        {
          color: colorReal,
          size: data.size, // ya viene en unidades del lienzo, igual que en el navegador
          highlighter: !!data.highlighter,
          thinning: data.thinning,
        },
        { alreadySmoothed: true },
      )
      ctx.renderer.invalidate()
      diag(shape ? `trazo guardado en el tablero (${Math.floor(shape.points.length / 3)} puntos)` : 'trazo convertido en figura u omitido')
      // El acuse sale después de que el lienzo pintara: el primer cuadro corre
      // junto al repintado que acabamos de pedir y el segundo ya salió a
      // pantalla. Solo entonces la capa nativa puede soltar su copia.
      if (typeof data.ackId === 'string' && data.ackId) {
        const ackId = data.ackId
        requestAnimationFrame(() => {
          requestAnimationFrame(() => post({ type: 'strokePainted', ackId }))
        })
      }
    },
    setNativeInk(active) {
      const next = !!active
      if (next === nativeInkActive) return
      nativeInkActive = next
      diag(`captura nativa del lápiz: ${nativeInkActive}`)
    },
    setPenDown(down) {
      nativePenDown = !!down
      if (!down) nativePenUpAt = performance.now()
    },
    undo: () => ctx.undo(),
    redo: () => ctx.redo(),
    setTool: (tool) => {
      const known = TOOLS.find((t) => t === tool)
      if (known) ctx.setTool(known)
    },
    state: () => JSON.stringify({ ...currentStyle(), zoom: ctx.store.camera.z, readOnly: ctx.store.readOnly }),
  }

  window.pizarraNative = api

  // El estado se avisa en cada cambio del store, y al arrastrar el lienzo eso
  // son más de cien veces por segundo. Solo se cruza el puente si de verdad
  // cambió algo: mover la vista no altera el estilo ni el zoom.
  let ultimo = ''
  const publish = (): void => {
    const payload = { type: 'style' as const, ...currentStyle(), zoom: ctx.store.camera.z, readOnly: ctx.store.readOnly }
    const serializado = JSON.stringify(payload)
    if (serializado === ultimo) return
    ultimo = serializado
    post(payload)
  }

  const unsubscribe = ctx.store.subscribe(publish)
  post({ type: 'ready', ...currentStyle(), zoom: ctx.store.camera.z, readOnly: ctx.store.readOnly })
  // El "ready" ya lleva el estado, así que se anota como enviado: si no, el
  // primer arrastre soltaba un "style" idéntico sin motivo.
  ultimo = JSON.stringify({ type: 'style' as const, ...currentStyle(), zoom: ctx.store.camera.z, readOnly: ctx.store.readOnly })

  /**
   * Al irse del tablero (galería, recarga, cierre) la capa nativa debe soltar
   * el lápiz: su estado sobrevive a la navegación y, si el próximo tablero
   * mandaba un "ready" idéntico, el nativo lo descartaba por repetido y nunca
   * volvía a avisar a la web → la web y el nativo dibujaban el MISMO trazo y
   * quedaba duplicado.
   */
  const despedirse = (): void => post({ type: 'style', tool: 'none', readOnly: true })
  window.addEventListener('pagehide', despedirse)

  return () => {
    unsubscribe()
    window.removeEventListener('pagehide', despedirse)
    despedirse()
    nativeInkActive = false
    nativePenDown = false
    delete window.pizarraNative
  }
}
